// frontend/src/lib/partner/partnerCurriculum.js

import { supabase } from "../../supabaseClient";

import { sanityClient } from "../sanity/sanityClient";



export const MAX_PARTNER_CURRICULUM_QUEST = 5;



const PARTNER_QUEST_OPTIONS_QUERY = `

  *[

    _type == "quest" &&

    !(_id in path("drafts.**")) &&

    defined(order) &&

    order >= 1 &&

    order <= $maxQuest

  ] | order(order asc) {

    _id,

    title,

    order,

    "slug": slug.current

  }

`;



const CURRICULUM_CATALOG_QUERY = `

  *[

    _type == "level" &&

    !(_id in path("drafts.**")) &&

    defined(legacyQuestId) &&

    defined(legacySubquestId) &&

    defined(legacyLevelKey) &&

    legacyQuestId in $legacyQuestIds

  ] {

    _id,

    title,

    "slug": slug.current,

    order,

    maxQuestions,

    xpMax,

    legacyQuestId,

    legacySubquestId,

    legacyLevelKey,

    "questTitle": subquest->quest->title,

    "questOrder": subquest->quest->order,

    "subquestTitle": subquest->title,

    "subquestOrder": subquest->order

  }

`;



const CURRICULUM_LEVEL_QUERY = `

  *[

    _type == "level" &&

    !(_id in path("drafts.**")) &&

    legacyQuestId in $legacyQuestIds &&

    legacySubquestId == $subquestId &&

    legacyLevelKey in $legacyLevelKeys

  ][0] {

    _id,

    title,

    "slug": slug.current,

    order,

    maxQuestions,

    xpMax,

    legacyQuestId,

    legacySubquestId,

    legacyLevelKey,

    "questTitle": subquest->quest->title,

    "questOrder": subquest->quest->order,

    "subquestTitle": subquest->title,

    "subquestOrder": subquest->order,



    resources[] {

      _key,

      title,

      description,

      resourceType,

      externalUrl,



      "fileUrl": file.asset->url,

      "fileName": file.asset->originalFilename,

      "fileMimeType": file.asset->mimeType,

      "fileSize": file.asset->size

    },



    steps[] {

      ...,



      illustration {

        asset->{

          url

        }

      },



      image {

        asset->{

          url

        }

      },



      options[] {

        ...,



        icon {

          asset->{

            url

          }

        }

      },



      pairs[] {

        ...

      },



      items[] {

        ...

      },



      targets[] {

        ...,



        icon {

          asset->{

            url

          }

        }

      }

    }

  }

`;



function cleanText(value) {

  return String(

    value ?? ""

  ).trim();

}



function requireId(

  value,

  label

) {

  const cleanValue =

    cleanText(value);



  if (!cleanValue) {

    throw new Error(

      `${label} is required.`

    );

  }



  return cleanValue;

}



function numberValue(

  value,

  fallback = 999999

) {

  const parsed =

    Number(value);



  return Number.isFinite(

    parsed

  )

    ? parsed

    : fallback;

}



function numberFromText(

  value,

  fallback = 999999

) {

  const match =

    cleanText(value).match(

      /\d+/

    );



  return match

    ? Number.parseInt(

        match[0],

        10

      )

    : fallback;

}



function compareByOrderThenTitle(

  left,

  right

) {

  const orderDifference =

    numberValue(

      left?.order

    ) -

    numberValue(

      right?.order

    );



  if (

    orderDifference !== 0

  ) {

    return orderDifference;

  }



  return cleanText(

    left?.title

  ).localeCompare(

    cleanText(

      right?.title

    ),

    "en",

    {

      sensitivity: "base",

    }

  );

}



export function normalisePartnerCurriculumQuestId(

  value

) {

  const cleanValue =

    cleanText(value)

      .toLowerCase();



  if (!cleanValue) {

    return "";

  }



  if (

    cleanValue ===

    "financial-foundations"

  ) {

    return "1";

  }



  const numberMatch =

    cleanValue.match(

      /\d+/

    );



  if (numberMatch) {

    return String(

      Number.parseInt(

        numberMatch[0],

        10

      )

    );

  }



  return cleanValue;

}



export function normalisePartnerCurriculumLevelKey(

  value

) {

  const cleanValue =

    cleanText(value);



  if (!cleanValue) {

    return "";

  }



  if (

    /^L\d+$/i.test(

      cleanValue

    )

  ) {

    return cleanValue.toUpperCase();

  }



  const numberMatch =

    cleanValue.match(

      /\d+/

    );



  if (numberMatch) {

    return `L${Number.parseInt(

      numberMatch[0],

      10

    )}`;

  }



  return cleanValue.toUpperCase();

}



function isSupportedQuestId(

  value

) {

  const questNumber =

    Number(

      normalisePartnerCurriculumQuestId(

        value

      )

    );



  return (

    Number.isInteger(

      questNumber

    ) &&

    questNumber >= 1 &&

    questNumber <=

      MAX_PARTNER_CURRICULUM_QUEST

  );

}



function buildLegacyQuestIds(

  questIds

) {

  return Array.from(

    new Set(

      questIds.flatMap(

        (value) => {

          const questId =

            normalisePartnerCurriculumQuestId(

              value

            );



          if (!questId) {

            return [];

          }



          return [

            questId,

            `Q${questId}`,

            `q${questId}`,

            `quest-${questId}`,

            `Quest ${questId}`,

          ];

        }

      )

    )

  );

}



function buildLegacyLevelKeys(

  levelKey

) {

  const normalised =

    normalisePartnerCurriculumLevelKey(

      levelKey

    );



  const levelNumber =

    numberFromText(

      normalised,

      0

    );



  if (!levelNumber) {

    return [

      normalised,

    ];

  }



  return Array.from(

    new Set([

      normalised,

      normalised.toLowerCase(),

      String(levelNumber),

      `level-${levelNumber}`,

      `Level ${levelNumber}`,

    ])

  );

}



function normaliseAccessRow(

  row

) {

  const questId =

    normalisePartnerCurriculumQuestId(

      row?.quest_id

    );



  if (

    !isSupportedQuestId(

      questId

    )

  ) {

    return null;

  }



  return {

    quest_id: questId,



    quest_order:

      numberValue(

        row?.quest_order,

        Number(questId)

      ),



    cohort_id:

      cleanText(

        row?.cohort_id

      ),



    cohort_name:

      cleanText(

        row?.cohort_name

      ) ||

      "Partner cohort",

  };

}



function normaliseCatalogLevel(

  row

) {

  const questId =

    normalisePartnerCurriculumQuestId(

      row?.legacyQuestId

    );



  const subquestId =

    cleanText(

      row?.legacySubquestId

    );



  const levelKey =

    normalisePartnerCurriculumLevelKey(

      row?.legacyLevelKey

    );



  if (

    !isSupportedQuestId(

      questId

    ) ||

    !subquestId ||

    !levelKey

  ) {

    return null;

  }



  return {

    id:

      row?._id ||

      `${questId}-${subquestId}-${levelKey}`,



    title:

      cleanText(

        row?.title

      ) ||

      `Level ${levelKey}`,



    slug:

      cleanText(

        row?.slug

      ),



    order:

      numberValue(

        row?.order,

        numberFromText(

          levelKey

        )

      ),



    max_questions:

      numberValue(

        row?.maxQuestions,

        0

      ),



    xp_max:

      numberValue(

        row?.xpMax,

        0

      ),



    quest_id:

      questId,



    quest_title:

      cleanText(

        row?.questTitle

      ) ||

      `Quest ${questId}`,



    quest_order:

      numberValue(

        row?.questOrder,

        Number(questId)

      ),



    subquest_id:

      subquestId,



    subquest_title:

      cleanText(

        row?.subquestTitle

      ) ||

      `Subquest ${subquestId}`,



    subquest_order:

      numberValue(

        row?.subquestOrder,

        numberFromText(

          subquestId

        )

      ),



    level_key:

      levelKey,

  };

}



function normaliseResource(

  resource,

  index

) {

  const externalUrl =

    cleanText(

      resource?.externalUrl

    );



  const fileUrl =

    cleanText(

      resource?.fileUrl

    );



  const url =

    fileUrl ||

    externalUrl;



  return {

    id:

      resource?._key ||

      `resource-${index}`,



    title:

      cleanText(

        resource?.title

      ) ||

      cleanText(

        resource?.fileName

      ) ||

      `Resource ${index + 1}`,



    description:

      cleanText(

        resource?.description

      ),



    resource_type:

      cleanText(

        resource?.resourceType

      ) ||

      (

        fileUrl

          ? "file"

          : "link"

      ),



    external_url:

      externalUrl,



    file_url:

      fileUrl,



    file_name:

      cleanText(

        resource?.fileName

      ),



    file_mime_type:

      cleanText(

        resource?.fileMimeType

      ),



    file_size:

      numberValue(

        resource?.fileSize,

        0

      ),



    url,

  };

}



function buildAccessMap(

  accessRows

) {

  const accessMap =

    new Map();



  accessRows.forEach(

    (row) => {

      if (

        !accessMap.has(

          row.quest_id

        )

      ) {

        accessMap.set(

          row.quest_id,

          new Map()

        );

      }



      accessMap

        .get(row.quest_id)

        .set(

          row.cohort_id,

          {

            id:

              row.cohort_id,



            name:

              row.cohort_name,

          }

        );

    }

  );



  return accessMap;

}



export async function fetchMyPartnerCurriculumAccess({

  cohortId = "",

} = {}) {

  const cleanCohortId =

    cleanText(cohortId);



  const {

    data,

    error,

  } = await supabase.rpc(

    "get_my_partner_curriculum_access"

  );



  if (error) {

    throw error;

  }



  return (

    Array.isArray(data)

      ? data

      : []

  )

    .map(

      normaliseAccessRow

    )

    .filter(Boolean)

    .filter(

      (row) =>

        !cleanCohortId ||

        row.cohort_id ===

          cleanCohortId

    );

}



export async function fetchPartnerQuestOptions() {

  const rows =

    (

      await sanityClient.fetch(

        PARTNER_QUEST_OPTIONS_QUERY,

        {

          maxQuest:

            MAX_PARTNER_CURRICULUM_QUEST,

        }

      )

    ) || [];



  const sanityMap =

    new Map(

      rows.map(

        (row) => [

          String(

            numberValue(

              row?.order,

              0

            )

          ),



          {

            id:

              String(

                numberValue(

                  row?.order,

                  0

                )

              ),



            title:

              cleanText(

                row?.title

              ),



            order:

              numberValue(

                row?.order,

                0

              ),



            slug:

              cleanText(

                row?.slug

              ),

          },

        ]

      )

    );



  return Array.from(

    {

      length:

        MAX_PARTNER_CURRICULUM_QUEST,

    },

    (_, index) => {

      const questId =

        String(

          index + 1

        );



      return (

        sanityMap.get(

          questId

        ) || {

          id: questId,

          title:

            `Quest ${questId}`,

          order:

            index + 1,

          slug: "",

        }

      );

    }

  );

}



export async function fetchPartnerCohortCurriculumAssignments(

  cohortId

) {

  const cleanCohortId =

    requireId(

      cohortId,

      "Cohort ID"

    );



  const {

    data,

    error,

  } = await supabase.rpc(

    "get_partner_cohort_curriculum_assignments",

    {

      p_cohort_id:

        cleanCohortId,

    }

  );



  if (error) {

    throw error;

  }



  const returnedRows =

    Array.isArray(data)

      ? data

      : [];



  const returnedMap =

    new Map(

      returnedRows.map(

        (row) => [

          normalisePartnerCurriculumQuestId(

            row?.quest_id

          ),

          row,

        ]

      )

    );



  return Array.from(

    {

      length:

        MAX_PARTNER_CURRICULUM_QUEST,

    },

    (_, index) => {

      const questId =

        String(

          index + 1

        );



      const row =

        returnedMap.get(

          questId

        );



      return {

        quest_id:

          questId,



        quest_order:

          index + 1,



        is_assigned:

          row?.is_assigned ===

          true,



        assigned_at:

          row?.assigned_at ||

          null,



        assigned_by:

          row?.assigned_by ||

          null,



        can_manage:

          row?.can_manage ===

          true,

      };

    }

  );

}



export async function setPartnerCohortCurriculumAssignments(

  cohortId,

  questIds

) {

  const cleanCohortId =

    requireId(

      cohortId,

      "Cohort ID"

    );



  const cleanQuestIds =

    Array.from(

      new Set(

        (

          Array.isArray(

            questIds

          )

            ? questIds

            : []

        )

          .map(

            normalisePartnerCurriculumQuestId

          )

          .filter(

            isSupportedQuestId

          )

      )

    ).sort(

      (

        left,

        right

      ) =>

        Number(left) -

        Number(right)

    );



  const {

    data,

    error,

  } = await supabase.rpc(

    "set_partner_cohort_curriculum_assignments",

    {

      p_cohort_id:

        cleanCohortId,



      p_quest_ids:

        cleanQuestIds,

    }

  );



  if (error) {

    throw error;

  }



  return Number(

    data || 0

  );

}



export async function fetchPartnerCurriculumCatalog({

  cohortId = "",

  reviewAll = false,

} = {}) {

  const accessRows =

    reviewAll

      ? []

      : await fetchMyPartnerCurriculumAccess(

          {

            cohortId,

          }

        );



  const allowedQuestIds =

    reviewAll

      ? Array.from(

          {

            length:

              MAX_PARTNER_CURRICULUM_QUEST,

          },

          (_, index) =>

            String(index + 1)

        )

      : Array.from(

          new Set(

            accessRows.map(

              (row) =>

                row.quest_id

            )

          )

        ).sort(

          (

            left,

            right

          ) =>

            Number(left) -

            Number(right)

        );



  if (

    allowedQuestIds.length ===

    0

  ) {

    return [];

  }



  const legacyQuestIds =

    buildLegacyQuestIds(

      allowedQuestIds

    );



  const rows =

    (

      await sanityClient.fetch(

        CURRICULUM_CATALOG_QUERY,

        {

          legacyQuestIds,

        }

      )

    ) || [];



  const accessMap =

    reviewAll

      ? new Map()

      : buildAccessMap(

          accessRows

        );



  const questMap =

    new Map();



  rows

    .map(

      normaliseCatalogLevel

    )

    .filter(Boolean)

    .filter(

      (level) =>

        allowedQuestIds.includes(

          level.quest_id

        )

    )

    .forEach(

      (level) => {

        if (

          !questMap.has(

            level.quest_id

          )

        ) {

          questMap.set(

            level.quest_id,

            {

              id:

                level.quest_id,



              title:

                level.quest_title,



              order:

                level.quest_order,



              assigned_cohorts:

                Array.from(

                  accessMap

                    .get(

                      level.quest_id

                    )

                    ?.values() ||

                    []

                ),



              subquestMap:

                new Map(),

            }

          );

        }



        const quest =

          questMap.get(

            level.quest_id

          );



        if (

          !quest.subquestMap.has(

            level.subquest_id

          )

        ) {

          quest.subquestMap.set(

            level.subquest_id,

            {

              id:

                level.subquest_id,



              title:

                level.subquest_title,



              order:

                level.subquest_order,



              levels: [],

            }

          );

        }



        quest.subquestMap

          .get(

            level.subquest_id

          )

          .levels.push(

            level

          );

      }

    );



  return Array.from(

    questMap.values()

  )

    .map(

      (quest) => ({

        id:

          quest.id,



        title:

          quest.title,



        order:

          quest.order,



        assigned_cohorts:

          quest.assigned_cohorts,



        subquests:

          Array.from(

            quest.subquestMap.values()

          )

            .map(

              (

                subquest

              ) => ({

                ...subquest,



                levels: [

                  ...subquest.levels,

                ].sort(

                  compareByOrderThenTitle

                ),

              })

            )

            .sort(

              compareByOrderThenTitle

            ),

      })

    )

    .sort(

      compareByOrderThenTitle

    );

}



export async function fetchPartnerCurriculumLevel({

  questId,

  subquestId,

  levelKey,

  cohortId = "",

  reviewAll = false,

}) {

  const cleanQuestId =

    normalisePartnerCurriculumQuestId(

      questId

    );



  const cleanSubquestId =

    requireId(

      subquestId,

      "Subquest ID"

    );



  const cleanLevelKey =

    normalisePartnerCurriculumLevelKey(

      levelKey

    );



  if (

    !isSupportedQuestId(

      cleanQuestId

    )

  ) {

    throw new Error(

      "Only Quests 1 to 5 are available in the Partner Portal."

    );

  }



  if (!cleanLevelKey) {

    throw new Error(

      "Level key is required."

    );

  }



  const accessRows =

    reviewAll

      ? []

      : await fetchMyPartnerCurriculumAccess(

          {

            cohortId,

          }

        );



  const questAccessRows =

    reviewAll

      ? []

      : accessRows.filter(

          (row) =>

            row.quest_id ===

            cleanQuestId

        );



  if (

    !reviewAll &&

    questAccessRows.length ===

    0

  ) {

    throw new Error(

      "This quest is not assigned to a cohort you can access."

    );

  }



  const level =

    await sanityClient.fetch(

      CURRICULUM_LEVEL_QUERY,

      {

        legacyQuestIds:

          buildLegacyQuestIds([

            cleanQuestId,

          ]),



        subquestId:

          cleanSubquestId,



        legacyLevelKeys:

          buildLegacyLevelKeys(

            cleanLevelKey

          ),

      }

    );



  if (!level) {

    return null;

  }



  return {

    ...level,



    quest_id:

      cleanQuestId,



    subquest_id:

      cleanSubquestId,



    level_key:

      cleanLevelKey,



    quest_title:

      cleanText(

        level?.questTitle

      ) ||

      `Quest ${cleanQuestId}`,



    subquest_title:

      cleanText(

        level?.subquestTitle

      ) ||

      `Subquest ${cleanSubquestId}`,



    assigned_cohorts:

      questAccessRows.map(

        (row) => ({

          id:

            row.cohort_id,



          name:

            row.cohort_name,

        })

      ),



    resources:

      (

        Array.isArray(

          level?.resources

        )

          ? level.resources

          : []

      ).map(

        normaliseResource

      ),



    steps:

      Array.isArray(

        level?.steps

      )

        ? level.steps

        : [],

  };

}